"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { MapIcon, HeadphonesIcon, BuildingIcon, GlobeIcon, ChevronDown, ChevronUp } from "lucide-react"

export default function ExampleUseCases() {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4 max-w-6xl">
        <div 
          className="flex items-center justify-center gap-3 mb-8 cursor-pointer group"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h2 className="text-3xl font-bold text-center">Example Use Cases</h2>
          <button
            className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            aria-label={isExpanded ? "Hide example use cases" : "Show example use cases"}
            aria-expanded={isExpanded}
          >
            {isExpanded ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
          </button>
        </div>

        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Sales Territories */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 flex flex-col">
            <div className="p-1 bg-blue-500"></div>
            <div className="p-6 flex-1">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <MapIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Sales Territories</h3>
              <p className="text-gray-600 mb-4">
                Define clear boundaries for your sales team to prevent territory overlap and ensure comprehensive market
                coverage.
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Assign reps to specific states or countries</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Color-code regions for easy identification</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Export maps for sales presentations</span>
                </li>
              </ul>
            </div>
            <div className="px-6 pb-6">
              <Link href="/sales-territory-maps">
                <Button variant="outline" className="w-full">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>

          {/* Field Service Management */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 flex flex-col">
            <div className="p-1 bg-purple-500"></div>
            <div className="p-6 flex-1">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <HeadphonesIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Field Service Management</h3>
              <p className="text-gray-600 mb-4">
                Optimize technician coverage areas to reduce travel time and improve response times for service calls.
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Map out service technician territories</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Balance workload across team members</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Identify coverage gaps in service areas</span>
                </li>
              </ul>
            </div>
            <div className="px-6 pb-6">
              <Link href="/support-coverage-map">
                <Button variant="outline" className="w-full">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>

          {/* Franchise Territories */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 flex flex-col">
            <div className="p-1 bg-green-500"></div>
            <div className="p-6 flex-1">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <BuildingIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Franchise Territories</h3>
              <p className="text-gray-600 mb-4">
                Clearly define franchise boundaries to prevent cannibalization and ensure fair market distribution.
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Map exclusive franchise territories</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Visualize expansion opportunities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Share maps with potential franchisees</span>
                </li>
              </ul>
            </div>
            <div className="px-6 pb-6">
              <Link href="/">
                <Button variant="outline" className="w-full">
                  Try It Now
                </Button>
              </Link>
            </div>
          </div>

          {/* Market Analysis */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 flex flex-col">
            <div className="p-1 bg-amber-500"></div>
            <div className="p-6 flex-1">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <GlobeIcon className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Market Analysis</h3>
              <p className="text-gray-600 mb-4">
                Visualize market penetration and identify growth opportunities across different geographic regions.
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Map current market presence</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Identify underserved regions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Plan strategic expansion</span>
                </li>
              </ul>
            </div>
            <div className="px-6 pb-6">
              <Link href="/">
                <Button variant="outline" className="w-full">
                  Try It Now
                </Button>
              </Link>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  )
}
